/**
 * Passkeys auf dem Gerät: Face ID statt Passwort.
 *
 * Die Schlüssel entstehen und bleiben im Schlüsselbund des Telefons; die App
 * reicht nur Challenge und Signatur zwischen Gerät und Server durch. Alles hier
 * geht nur online — eine Anmeldung, die offline „klappt", gibt es nicht.
 */

import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { apiFetch } from './api.js';
import type { SessionUser } from './auth.js';

export interface PasskeySummary {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export function passkeysSupported(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/**
 * Abgebrochen (Face ID weggewischt, Zeit abgelaufen) ist kein Fehler, den man
 * rot anzeigen muss — der Nutzer weiß, dass er abgebrochen hat.
 */
export function isCancelled(error: unknown): boolean {
  const name = (error as { name?: string; cause?: { name?: string } } | null)?.name;
  const cause = (error as { cause?: { name?: string } } | null)?.cause?.name;
  return name === 'NotAllowedError' || name === 'AbortError' || cause === 'NotAllowedError';
}

/** Dieses Gerät hat schon einen Passkey für das Konto. */
export function isAlreadyRegistered(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  const name = (error as { name?: string } | null)?.name;
  return code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED' || name === 'InvalidStateError';
}

export async function signInWithPasskey(): Promise<{ user: SessionUser }> {
  const { flowId, options } = await apiFetch<{
    flowId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>('/auth/passkey/options', { method: 'POST', body: '{}' });
  const response = await startAuthentication({ optionsJSON: options });
  return apiFetch<{ user: SessionUser }>('/auth/passkey/login', {
    method: 'POST',
    body: JSON.stringify({ flowId, response }),
  });
}

export async function listPasskeys(): Promise<PasskeySummary[]> {
  return (await apiFetch<{ passkeys: PasskeySummary[] }>('/passkeys')).passkeys;
}

export async function addPasskey(input: {
  password: string;
  name: string;
}): Promise<PasskeySummary> {
  const { flowId, options } = await apiFetch<{
    flowId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }>('/passkeys/options', {
    method: 'POST',
    body: JSON.stringify({ password: input.password }),
  });
  const response = await startRegistration({ optionsJSON: options });
  const saved = await apiFetch<{ passkey: PasskeySummary }>('/passkeys', {
    method: 'POST',
    body: JSON.stringify({ flowId, name: input.name, response }),
  });
  return saved.passkey;
}

export async function removePasskey(id: string): Promise<void> {
  await apiFetch<undefined>(`/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
